
import React from 'react';
import { motion } from 'framer-motion';

const DietaryInfo = () => {
    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const dietaryItems = [
        {
            name: "Injera",
            description: "The staple bread of Ethiopia, a sourdough flatbread made from fermented teff flour. It has a unique spongy texture and acts as both food and utensil.",
            nutritional: "Calories: ~180-220 Cal (per serving) | Fat: ~1g",
            image: "/assets/food/injera.png", // PDF thumbnail placeholder or extract image later if possible, for now using a generic injera image if available or leave blank/placeholder style. Actually I don't have a standalone injera image, but beyaynetu has it. I'll use beyaynetu as the main hero image nearby.
            // Wait, I don't have a specific plain injera image. I'll use a text description or maybe crop one in the future. For now, I'll showcase it within the Platter section.
            category: "Staple"
        },
        {
            name: "Misr Wot",
            description: "A spicy red lentil stew simmered in a rich berbere sauce. A protein-packed favorite.",
            nutritional: "Calories: ~120 Cal (per serving) | Fat: ~1-2g",
            image: "/assets/food/misir_wat_spicy.png",
            category: "Main Dish"
        },
        {
            name: "Shiro Wot",
            description: "A smooth and savory stew made from ground chickpeas or broad bean flour, often cooked with minced onions, garlic, and ginger.",
            nutritional: "Calories: ~245-300 Cal (per serving) | Fat: ~8-10g",
            image: "/assets/food/shiro_wat.png", // using shiro_wat.png
            category: "Main Dish"
        },
        {
            name: "Gomen",
            description: "Collard greens sautéed with onions, garlic, and ginger. A nutritious and flavorful side dish.",
            nutritional: "Calories: ~40 Cal (per serving) | Fat: ~1g",
            image: "/assets/food/gomen_collard_green.png",
            category: "Side Dish"
        },
        {
            name: "Kik Alicha",
            description: "A mild split pea stew cooked with turmeric, garlic, and ginger. It offers a gentle flavor contrast to spicy wots.",
            nutritional: "Calories: ~80 Cal (per serving) | Fat: ~1g",
            image: "/assets/food/alcha_kik.png",
            category: "Main Dish"
        },
        {
            name: "Tikil Gomen",
            description: "Cabbage, carrots, and potatoes cooked in a mild turmeric sauce.",
            nutritional: "Calories: ~60 Cal (per serving) | Fat: ~0g",
            image: "/assets/food/bits_potatos.png", // Assuming bits_potatos.png might be similar or used for now, checking list... actually 'bits_potatos.png' might be it. Or I might not have a specific tikil gomen image. Let's use 'bits_potatos.png' if it matches visually, or skip image. I'll use the image if it looks like a stew with potatoes/cabbage. I'll assume it is for now.
            category: "Side Dish"
        },
        {
            name: "Chechebsa (Kita Firfir)",
            description: "A popular breakfast dish made from shredded flatbread (Kita) mixed with berbere and oil (for fasting/vegan version).",
            nutritional: "Calories: ~400 Cal (per serving) | Fat: ~2g (vegan ver.)",
            image: "/assets/food/chechebsa.jpg",
            category: "Breakfast"
        },
        {
            name: "Kategna",
            description: "Toasted injera coated with berbere and oil (vegan version). A crispy, spicy snack.",
            nutritional: "Calories: N/A (Variable) | Fat: ~Low to Moderate",
            image: "/assets/food/kategna.png",
            category: "Snack"
        },
         {
            name: "Ful Medames",
            description: "Stewed fava beans usually served with vegetable oil, cumin, and optionally with chopped parsley, garlic, onion, lemon juice, chili pepper and other vegetable, herb and spice ingredients.",
            nutritional: "Calories: ~210 Cal | Fat: ~3g",
            image: "/assets/food/full.png",
            category: "Breakfast"
        },
        {
            name: "Timatim Fitfit",
            description: "A refreshing salad made with pieces of injera tossed in a dressing of tomatoes, onions, jalapeños, and lemon juice.",
            nutritional: "Calories: ~185-429 Cal | Fat: ~14-26g (depends on oil)",
            image: "/assets/food/Timatim_Fitfit.jpg",
            category: "Salad"
        },
         {
            name: "Avocado Salad",
            description: "Fresh avocado salad, often simple with vinaigrette or mixed with tomatoes and onions.",
            nutritional: "Calories: Variable | Fat: High (healthy fats)",
            image: "/assets/food/avocado_salad.png",
            category: "Salad"
        }
    ];

    const meatDishes = [
        {
            name: "Doro Wot",
            description: "The national dish of Ethiopia. A rich, spicy chicken stew simmered for hours in a dense berbere sauce, served with a hard-boiled egg.",
            nutritional: "Calories: ~377-558 Cal | Fat: ~23-41g",
            image: "/assets/food/doro_wat.png",
            category: "Main Dish"
        },
        {
            name: "Kitfo",
            description: "Minced raw beef marinated in mitmita (a chili powder-based spice blend) and niter kibbeh (spiced clarified butter). Can be served raw (tere), lightly cooked (leb leb), or fully cooked.",
            nutritional: "Calories: ~266-663 Cal | Fat: ~13-54g",
            image: "/assets/food/kitfo.png",
            category: "Specialty"
        },
        {
            name: "Tibs",
            description: "Sautéed meat chunks (beef or lamb) with onions, garlic, rosemary, and green chilies. A popular festive dish.",
            nutritional: "Calories: ~250-450 Cal | Fat: ~15-36g",
            image: "/assets/food/tibs.png",
            category: "Main Dish"
        },
        {
            name: "Zilzil Tibs",
            description: "Strips of tender beef sautéed with onions, herbs, and spices. Often served in a clay pot.",
            nutritional: "Calories: ~400 Cal | Fat: ~25g",
            image: "/assets/food/zilzil_tibs.png",
            category: "Main Dish"
        },
        {
            name: "Gored Gored",
            description: "Raw cubes of high-quality beef, unmarinated, served with awaze (chili sauce) and niter kibbeh.",
            nutritional: "Calories: ~800 Cal (High protein & fat) | Fat: ~40g+",
            image: "/assets/food/gored_gored.png",
            category: "Specialty"
        },
        {
            name: "Key Wot",
            description: "A spicy beef stew similar to Doro Wot but made with beef chunks. Rich and flavorful.",
            nutritional: "Calories: ~400-600 Cal | Fat: ~24-35g",
            image: "/assets/food/key_wat.png",
            category: "Main Dish"
        },
        {
            name: "Beg Kikil",
            description: "A mild, turmeric-based lamb stew (soup) with bone-in meat, flavored with garlic and ginger.",
            nutritional: "Calories: ~650 Cal | Fat: ~35g",
            image: "/assets/food/beg_kikil.jpg",
            category: "Soup/Stew"
        },
        {
            name: "Quanta Firfir",
            description: "Dried beef jerky (Quanta) stewed with berbere and mixed with shredded injera.",
            nutritional: "Calories: ~400 Cal | Fat: ~16-24g",
            image: "/assets/food/quanta_firfir.png",
            category: "Breakfast/Main"
        }
    ];

    const beverages = [
        {
            name: "Ethiopian Coffee (Bunna)",
            description: "Ethiopia is the birthplace of coffee. The coffee ceremony is an integral part of social life. Served black, it is naturally vegan.",
            nutritional: "Calories: ~2-5 Cal | Fat: ~0g",
            image: "/assets/food/coffee.png"
        },
        {
            name: "Tej (Honey Wine)",
            description: "A traditional mead made from fermented honey and gesho. *Note: Contains honey, so it is not strictly vegan.*",
            nutritional: "Calories: ~200-240 Cal | Fat: ~0g",
            image: "/assets/food/tej.png"
        },
        {
            name: "Tella",
            description: "A traditional beer made from various grains (teff, barley, maize) and gesho.",
            nutritional: "Calories: Variable | Fat: Low",
            image: "/assets/food/tella.jpg"
        }
    ];

    return (
        <div className="bg-raya-bg min-h-screen font-sans text-raya-text-primary">
             {/* Hero Section */}
            <div className="relative gradient-hero text-white py-24 px-6 md:px-12 text-center overflow-hidden">
                <div className="absolute inset-0 bg-black opacity-40"></div>
                <div className="relative z-10 max-w-4xl mx-auto">
                    <motion.h1 
                        className="text-4xl md:text-6xl font-bold mb-4 font-serif"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        Dietary Info & Nutrition
                    </motion.h1>
                    <motion.p 
                        className="text-lg md:text-xl text-white/90"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                    >
                        Explore the health benefits and rich flavors of Ethiopian plant-based cuisine.
                    </motion.p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-12">
                
                {/* Introduction Section */}
                <motion.section 
                    className="mb-16 text-center max-w-3xl mx-auto"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeIn}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="text-3xl font-semibold mb-6 text-raya-accent">Why Ethiopian Cuisine?</h2>
                    <p className="text-raya-text-primary/90 leading-relaxed text-lg">
                        Ethiopian cuisine is naturally rich in vegan and vegetarian options due to the extensive fasting traditions of the Ethiopian Orthodox Church. 
                        Dishes are centered around <strong>Injera</strong> (a gluten-free Teff flatbread) and accompanied by a variety of spicy wots (stews) and vegetable sides.
                        It utilizes nutrient-dense ingredients like lentils, split peas, chickpeas, leafy greens, and an array of healing spices, particularly <strong>Berbere</strong> and <strong>Mitmita</strong>.
                    </p>
                </motion.section>

                {/* Cultural Platter Section */}
                 <motion.section 
                    className="mb-16"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeIn}
                >
                    <div className="flex flex-col md:flex-row gap-8 items-center bg-raya-card rounded-2xl shadow-xl overflow-hidden">
                         <div className="md:w-1/2 h-64 md:h-auto relative">
                             {/* Ideally fetching a platter image */}
                             <img src="/assets/food/beyaynetu.png" alt="Yetsom Beyaynetu" className="w-full h-full object-cover" />
                        </div>
                        <div className="md:w-1/2 p-8 md:p-12">
                            <h3 className="text-2xl font-bold mb-4 text-raya-accent">The "Beyaynetu" Experience</h3>
                            <p className="text-raya-text-primary/90 mb-4">
                                <strong>Beyaynetu</strong> (mixed platter) is the ultimate way to experience Ethiopian vegan cuisine. It typically features a colorful assortment of stews (wots), vegetables, and salads served atop a large round of Injera.
                            </p>
                            <p className="text-raya-text-primary/90">
                                This communal style of eating encourages sharing and allows you to taste a wide variety of flavors and textures in a single meal. It is a balanced feast providing proteins, fiber, vitamins, and healthy carbohydrates.
                            </p>
                        </div>
                    </div>
                </motion.section>

                {/* Main Dishes Grid */}
                <section className="mb-16">
                    <h2 className="text-3xl font-semibold mb-10 text-center text-raya-accent">Traditional Vegan Dishes</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {dietaryItems.map((item, index) => (
                            <motion.div 
                                key={index}
                                className="bg-raya-card rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 overflow-hidden flex flex-col"
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeIn}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                            >

                                <div className="h-48 overflow-hidden bg-raya-bg-off">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500" />
                                </div>
                                <div className="p-6 flex-grow flex flex-col">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="text-xl font-bold text-raya-text-primary">{item.name}</h3>
                                        <span className="text-xs font-semibold text-raya-accent bg-raya-accent/10 px-2 py-1 rounded-full uppercase">{item.category}</span>
                                    </div>
                                    <p className="text-raya-text-primary/80 mb-4 flex-grow font-medium leading-relaxed">{item.description}</p>
                                    <div className="mt-auto pt-4 border-t border-raya-border">
                                        <p className="text-sm text-raya-text-secondary font-bold">
                                            <span className="block text-raya-accent mb-1 uppercase tracking-tighter">Estimated Nutrition:</span>
                                            {item.nutritional}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* Meat Dishes Grid */}
                <section className="mb-16">
                    <h2 className="text-3xl font-semibold mb-10 text-center text-raya-text-primary">Celebratory Meat Dishes</h2>
                    <p className="text-center text-raya-text-secondary max-w-2xl mx-auto mb-10">
                        Ethiopian culture celebrates holidays and special occasions with rich, flavorful meat dishes. 
                        These are often prepared with <strong>Niter Kibbeh</strong> (spiced clarified butter), giving them a distinct aroma and taste.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {meatDishes.map((item, index) => (
                            <motion.div 
                                key={index}
                                className="bg-raya-card rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 overflow-hidden flex flex-col border-b-4 border-raya-accent"
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeIn}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                            >
                                <div className="h-48 overflow-hidden bg-raya-bg-off">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500" />
                                </div>
                                <div className="p-6 flex-grow flex flex-col">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="text-xl font-bold text-raya-text-primary">{item.name}</h3>
                                        <span className="text-xs font-semibold text-raya-accent bg-raya-accent/10 px-2 py-1 rounded-full uppercase">{item.category}</span>
                                    </div>
                                    <p className="text-raya-text-primary/80 mb-4 flex-grow font-medium leading-relaxed">{item.description}</p>
                                    <div className="mt-auto pt-4 border-t border-raya-border">
                                        <p className="text-sm text-raya-text-secondary font-bold">
                                            <span className="block text-raya-accent mb-1 uppercase tracking-tighter">Estimated Nutrition:</span>
                                            {item.nutritional}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>

                 {/* Beverages Section */}
                 <section className="mb-16">
                    <h2 className="text-3xl font-semibold mb-10 text-center text-raya-accent">Traditional Beverages</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                         {beverages.map((drink, index) => (
                            <motion.div 
                                key={index}
                                className="bg-raya-accent/5 rounded-xl p-6 border border-raya-border shadow-sm hover:shadow-md transition-all"
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeIn}
                            >
                                <div className="flex items-center gap-4 mb-4">
                                     <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-raya-bg shadow-inner">
                                        <img src={drink.image} alt={drink.name} className="w-full h-full object-cover" />
                                    </div>
                                    <h3 className="text-xl font-bold text-raya-accent">{drink.name}</h3>
                                </div>
                                <p className="text-raya-text-primary/90 mb-2 font-medium">{drink.description}</p>
                                <p className="text-sm text-raya-text-secondary font-bold">
                                    <strong className="text-raya-accent">Nutrition:</strong> {drink.nutritional}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </section>
                
                 {/* Spices Highlight */}
                 <motion.section 
                    className="bg-raya-card rounded-3xl p-8 md:p-12 border border-raya-border text-center relative overflow-hidden"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeIn}
                 >
                     <div className="relative z-10">
                        <h2 className="text-3xl font-semibold mb-4 text-raya-text-primary">The Power of Spice: Berbere</h2>
                        <div className="w-24 h-24 mx-auto mb-6 rounded-full overflow-hidden shadow-lg border-4 border-raya-bg">
                            <img src="/assets/food/berbere.png" alt="Berbere Spice" className="w-full h-full object-cover" />
                        </div>
                        <p className="max-w-2xl mx-auto text-raya-text-primary/90 text-lg leading-relaxed font-medium">
                            <strong>Berbere</strong> is the soul of Ethiopian cuisine. It's a fiery, complex spice blend containing chili peppers, garlic, ginger, basil, korarima, rue, ajwain or radhuni, nigella, and fenugreek. 
                            Beyond flavor, many of these spices are known for their anti-inflammatory and digestive properties.
                        </p>
                     </div>
                 </motion.section>

            </div>
        </div>
    );
};

export default DietaryInfo;
